import { useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Mail, Globe, ArrowUp, ArrowRight, Loader2 } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { useLang } from "@/context/LangContext";

const LOGO_WHITE_URL = "https://customer-assets.emergentagent.com/job_secure-certified/artifacts/yt15bzto_gaca-logo-white.svg";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const serviceLinks = [
  { label: "ISO/IEC 27001", href: "/services" },
  { label: "ISO 9001", href: "/services" },
  { label: "ISO 22301", href: "/services" },
  { label: "ISO 27701", href: "/services" },
  { label: "Compliance Assessments", href: "/services" },
  { label: "Cybersecurity Assurance", href: "/services" },
];

const companyLinks = [
  { key: "about", href: "/about" },
  { key: "process", href: "/process" },
  { key: "accreditations", href: "/accreditations" },
  { key: "why_gaca", href: "/why-gaca" },
  { key: "industries", href: "/industries" },
  { key: "training", href: "/training" },
  { key: "insights", href: "/insights" },
  { key: "careers", href: "/careers" },
  { key: "faq", href: "/faq" },
  { key: "apply", href: "/contact" },
];

const legalLinks = [
  { label: "Privacy Policy", href: "/privacy-policy" },
  { label: "Terms of Certification", href: "/terms-of-certification" },
  { label: "Impartiality Policy", href: "/impartiality-policy" },
];

function FooterBrand({ t }) {
  return (
    <div className="lg:col-span-1">
      <img src={LOGO_WHITE_URL} alt="GACA International" className="h-14 w-auto mb-5" data-testid="footer-logo" />
      <p className="text-white/50 text-xs leading-relaxed mb-5">
        {t?.footer?.desc || "Global Assurance & Certification Authority. Independent certification board delivering ISO certification through accredited partner bodies."}
      </p>
      <div className="space-y-2">
        <a href="mailto:certification@gacaint.com" data-testid="footer-email-cert" className="flex items-center gap-2 text-xs text-white/50 hover:text-[#13A8BD] transition-colors"><Mail size={12} /> certification@gacaint.com</a>
        <a href="mailto:info@gacaint.com" data-testid="footer-email-info" className="flex items-center gap-2 text-xs text-white/50 hover:text-[#13A8BD] transition-colors"><Mail size={12} /> info@gacaint.com</a>
        <a href="https://gacaint.com" data-testid="footer-website" className="flex items-center gap-2 text-xs text-white/50 hover:text-[#13A8BD] transition-colors"><Globe size={12} /> gacaint.com</a>
      </div>
    </div>
  );
}

function FooterServices({ t }) {
  return (
    <div>
      <h4 className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#13A8BD] mb-5">{t?.footer?.services || "Services"}</h4>
      <ul className="space-y-2">
        {serviceLinks.map((s) => (
          <li key={s.label}><Link to={s.href} className="text-xs text-white/50 hover:text-white transition-colors">{s.label}</Link></li>
        ))}
      </ul>
    </div>
  );
}

function FooterCompany({ t }) {
  const navLabel = (key, fallback) => t?.nav?.[key] || fallback;
  return (
    <div>
      <h4 className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#13A8BD] mb-5">{t?.footer?.company || "Company"}</h4>
      <ul className="space-y-2">
        {companyLinks.slice(0, 7).map((link) => (
          <li key={link.href + link.key}><Link to={link.href} className="text-xs text-white/50 hover:text-white transition-colors">{navLabel(link.key, link.key)}</Link></li>
        ))}
      </ul>
    </div>
  );
}

function FooterResources({ t }) {
  const navLabel = (key, fallback) => t?.nav?.[key] || fallback;
  return (
    <div>
      <h4 className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#13A8BD] mb-5">{t?.footer?.resources || "Resources"}</h4>
      <ul className="space-y-2">
        {companyLinks.slice(7).map((link) => (
          <li key={link.href + link.key}><Link to={link.href} className="text-xs text-white/50 hover:text-white transition-colors">{navLabel(link.key, link.key)}</Link></li>
        ))}
      </ul>
      <h4 className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#13A8BD] mt-6 mb-4">{t?.footer?.legal || "Legal"}</h4>
      <ul className="space-y-2">
        {legalLinks.map((link) => (
          <li key={link.href}><Link to={link.href} data-testid={`footer-legal-${link.label.toLowerCase().replace(/\s+/g, '-')}`} className="text-xs text-white/50 hover:text-white transition-colors">{link.label}</Link></li>
        ))}
      </ul>
    </div>
  );
}

function FooterNewsletter({ t }) {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await axios.post(`${API}/newsletter`, { email });
      toast.success("Subscribed successfully.");
      setEmail("");
    } catch {
      toast.error("Please enter a valid email address.");
    } finally {
      setLoading(false);
    }
  }, [email, setLoading, setEmail]);

  return (
    <div>
      <h4 className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#13A8BD] mb-5">{t?.footer?.newsletter || "Newsletter"}</h4>
      <p className="text-xs text-white/50 mb-3">{t?.footer?.newsletter_desc || "Get certification and cybersecurity insights."}</p>
      <form onSubmit={handleSubmit} className="flex" data-testid="newsletter-form">
        <input
          type="email" value={email} onChange={(e) => setEmail(e.target.value)}
          placeholder={t?.footer?.subscribe_placeholder || "Your email"}
          data-testid="newsletter-email-input"
          className="flex-1 px-3 py-2 bg-white/10 border border-white/15 text-white text-xs placeholder:text-white/30 focus:outline-none focus:border-[#13A8BD]"
        />
        <button type="submit" disabled={loading} data-testid="newsletter-submit-button" className="px-3 py-2 bg-[#13A8BD] text-white hover:bg-[#0e8a9c] transition-colors">
          {loading ? <Loader2 size={14} className="animate-spin" /> : <ArrowRight size={14} />}
        </button>
      </form>
    </div>
  );
}

function FooterBottom({ t }) {
  const scrollToTop = () => window.scrollTo({ top: 0, behavior: "smooth" });
  return (
    <div className="border-t border-white/10">
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-5 flex flex-col md:flex-row items-center justify-between gap-4">
        <p className="text-[10px] text-white/35" data-testid="footer-copyright">
          &copy; {t?.footer?.copyright || "2026 GACA International. All rights reserved. Global Assurance & Certification Authority."}
        </p>
        <div className="flex items-center gap-6">
          {legalLinks.map((link) => (
            <Link key={link.href} to={link.href} className="text-[10px] text-white/35 hover:text-white/60 transition-colors">{link.label}</Link>
          ))}
          <button onClick={scrollToTop} data-testid="scroll-to-top" className="p-1.5 border border-white/15 text-white/35 hover:text-white hover:border-white/30 transition-colors">
            <ArrowUp size={14} />
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Footer() {
  const { t } = useLang();
  return (
    <footer data-testid="footer" className="bg-[#0B2545] text-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-16 lg:py-20">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-10 lg:gap-12">
          <FooterBrand t={t} />
          <FooterServices t={t} />
          <FooterCompany t={t} />
          <FooterResources t={t} />
          <FooterNewsletter t={t} />
        </div>
      </div>
      <FooterBottom t={t} />
    </footer>
  );
}
